import { clsx } from "clsx";
import type { FC } from "react";
import Select from "react-select";
import { type BaseFieldArrayProps, FieldArray, type WrappedFieldArrayProps } from "redux-form";
import type { Validator } from "redux-form/lib/Field";

import { Callout, SymbolIcon, TriggerButton, TriggerButtonIntent } from "widgets";

import style from "../../../styles/callout.module.scss";

import type { ISelectOption } from "../../../app-utils/form.js";
import { type Attribute, type ExportWorkflowField, NfParamSubTypeEnum } from "../../../graphql.js";
import { getAttributeLabel } from "../../../redux/modules/sample-admin.js";
import { ATTRIBUTE_TYPE, WORKFLOW_FIELD_TYPE_OPTIONS } from "../../../settings/settings.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import { PlaybookConfigNgsResultsScreenImpl } from "./PlaybookConfigNgsResultsScreen.js";
import { attributeFileTypeMatchesNfParamSubType } from "@/app-utils/playbook.ts";

export interface IExportedWorkflowFieldMapperProps extends Partial<WrappedFieldArrayProps> {
  availableExportFields?: MapperWorkflowField[];
  sampleAttributes?: Attribute[];
  isLoadingAttributes?: boolean;
  isInputMappingContext?: boolean;
}

export interface MapperWorkflowField extends ExportWorkflowField {
  subType?: NfParamSubTypeEnum;
  label?: string;
}

const extractFieldArrayIndexFromName = (name: string) => {
  const match = name.match(/\d+/);
  if (match && match.length) {
    return Number.parseInt(match[0], 10);
  }
};

const validateMappingError: Validator = (attribute: Attribute, allValues, _allProps, fieldName) => {
  const index = extractFieldArrayIndexFromName(fieldName);
  const fieldMappingsArray = allValues[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS];
  const field = fieldMappingsArray?.[index];
  const workflowField: MapperWorkflowField =
    field?.[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD];

  if (!workflowField) {
    return;
  }
  if (!attribute) {
    return `A selection is required.`;
  }
  const workflowFieldType: ISelectOption = WORKFLOW_FIELD_TYPE_OPTIONS.find(
    (option) => option.value === workflowField.typeId,
  );
  if (
    attribute.attributeType.id !== ATTRIBUTE_TYPE.FREE_NUMERICAL &&
    attribute.attributeType.id !== ATTRIBUTE_TYPE.FREE_TEXT &&
    attribute.attributeType.id !== ATTRIBUTE_TYPE.FILE
  ) {
    return `Mapping to a tracker attribute of type '${attribute.attributeType.name}' is currently not supported`;
  } else if (attribute.automatic) {
    return `Mapping to an automatic tracker attribute is not allowed`;
  } else if (
    attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL &&
    workflowFieldType.value === ATTRIBUTE_TYPE.FREE_TEXT
  ) {
    return `Types are incompatible: '${workflowFieldType.label}' to '${attribute.attributeType.name}'`;
  } else if (attribute.attributeType.id !== ATTRIBUTE_TYPE.FILE && workflowFieldType.value === ATTRIBUTE_TYPE.FILE) {
    return `Types are incompatible: '${workflowFieldType.label}' to '${attribute.attributeType.name}'`;
  } else if (workflowField.isMultiValues && !attribute.multipleValues) {
    return `The selected Workflow field does export multiple values while this Sample Attribute does not support multiple values`;
  }
};

const validateMappingWarning = (attribute: Attribute, allValues, _allProps, fieldName) => {
  const index = extractFieldArrayIndexFromName(fieldName);
  const fieldMappingsArray = allValues[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS];
  const field = fieldMappingsArray?.[index];
  const workflowField: MapperWorkflowField =
    field?.[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD];

  if (!workflowField || !attribute) {
    return;
  }
  const workflowFieldType: ISelectOption = WORKFLOW_FIELD_TYPE_OPTIONS.find(
    (option) => option.value === workflowField.typeId,
  );
  if (attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT && workflowFieldType.value !== ATTRIBUTE_TYPE.FREE_TEXT) {
    return (
      <span>
        Mapping from '{workflowFieldType.label}' to '{attribute.attributeType.name}' is not recommended
      </span>
    );
  }
};

const ExportedWorkflowFieldMapper: FC<IExportedWorkflowFieldMapperProps> = (props) => {
  const selectedAttributes = props.fields
    .map(
      (_member, index) =>
        props.fields.get(index)[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE],
    )
    .filter((attribute: Attribute) => !!attribute); // some mapping might be missing the attribute, not yet selected

  const selectedWorkflowExportFields = props.fields.map(
    (_member, index) =>
      props.fields.get(index)[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD],
  );

  const exportFieldsOptions = props.availableExportFields.filter((option: MapperWorkflowField) => {
    return !selectedWorkflowExportFields.find((selectedExportWorkflowField: MapperWorkflowField) => {
      return selectedExportWorkflowField.name === option.name;
    });
  });

  return (
    <div className={"workflow-field-mapper"}>
      <div className={"workflow-field-mapper-row"}>
        <div style={{ width: "45%" }}>
          <label style={{ fontWeight: "bold" }}>
            {props.isInputMappingContext ? "Nextflow Parameter" : "NGS workflow output"}
          </label>
        </div>
        <div style={{ width: "55%" }}>
          <label style={{ fontWeight: "bold" }}>Sample attribute</label>
        </div>
      </div>

      {props.fields.length === 0 && (
        <div>
          {props.isInputMappingContext
            ? "You must define at least one mapping for a Nextflow parameter."
            : "You have not mapped any NGS outputs. Data will not be saved to the sample records in the database."}
        </div>
      )}

      {props.fields.map((member, index) => {
        const mapping = props.fields.get(index);
        const workflowField: MapperWorkflowField =
          mapping?.[PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD];

        const sampleAttributeOptions = props.sampleAttributes.filter((option: Attribute) => {
          return (
            !selectedAttributes.find((selectedAttribute: Attribute) => {
              return selectedAttribute.id === option.id;
            }) &&
            (!props.isInputMappingContext ||
              attributeFileTypeMatchesNfParamSubType(option.fileType.id, workflowField.subType))
          );
        });

        // sort alphabetically
        sampleAttributeOptions.sort((attr1, attr2) =>
          getAttributeLabel(attr1).localeCompare(getAttributeLabel(attr2), "en", { sensitivity: "base" }),
        );

        return (
          <div className={"workflow-field-mapper-row"} key={index}>
            <div style={{ width: "35%", overflowWrap: "break-word" }}>{workflowField.label || workflowField.name}</div>
            <div style={{ width: "10%", display: "inline-flex", justifyContent: "center" }}>
              <SymbolIcon icon={"arrow_circle_right"} className="mapper-row-icon" />
            </div>
            <div style={{ width: "50%" }}>
              <ReactSelectReduxFormField
                name={`${member}.${PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE}`}
                formGroupClassName={"workflow-field-mapper-row-attribute"}
                isClearable={true}
                isSearchable={true}
                getOptionValue={(option: Attribute) => option.id}
                getOptionLabel={getAttributeLabel}
                horizontalLayout={true}
                placeholder={"Map to sample attribute..."}
                component={ReactSelectField}
                isLoading={props.isLoadingAttributes}
                options={sampleAttributeOptions}
                isDisabled={false}
                validate={validateMappingError}
                warn={validateMappingWarning}
                noOptionsMessage={() => "No available attribute..."}
              />
            </div>
            <TriggerButton
              label={"remove mapping"}
              icon={"delete"}
              style={{ width: "5%" }}
              intent={TriggerButtonIntent.Outlined}
              onPress={() => props.fields.remove(index)}
            ></TriggerButton>
          </div>
        );
      })}

      <Select
        isClearable={false}
        isSearchable={true}
        getOptionValue={(option: MapperWorkflowField) => option.name}
        getOptionLabel={(option: MapperWorkflowField) =>
          `${option.label || option.name} ${option.description ? "(" + option.description + ")" : ""}`
        }
        isDisabled={false}
        isLoading={false}
        options={exportFieldsOptions}
        className={"workflow-field-mapper-add"}
        classNamePrefix={"react-select"}
        placeholder={
          props.isInputMappingContext ? "Select a Nextflow parameter..." : "Select an NGS workflow output..."
        }
        onChange={(exportField: MapperWorkflowField) =>
          props.fields.push({
            [PlaybookConfigNgsResultsScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD]: exportField,
          })
        }
        noOptionsMessage={() => "No available attribute..."}
        controlShouldRenderValue={false}
        onBlurResetsInput={true}
        menuPosition={"absolute"}
        menuPlacement={"auto"}
        menuPortalTarget={document.body}
        menuShouldScrollIntoView={false}
        styles={{ menuPortal: (provided) => ({ ...provided, zIndex: 9999 }) }}
      />

      {props.meta.invalid && <Callout className={clsx(style.oldError)}>{props.meta.error}</Callout>}
    </div>
  );
};

export default ExportedWorkflowFieldMapper;

export function ExportedWorkflowFieldMapperReduxFormField(
  props: IExportedWorkflowFieldMapperProps & BaseFieldArrayProps,
) {
  return <FieldArray {...props} rerenderOnEveryChange={true} />;
}
